import type { ReactNode } from "react";
import { hideModal, showConfirmModal } from "actions/modal";
import { navigateToUrl } from "./browser-navigation";

/**
 * Narrow dispatch contract: this helper only ever dispatches the modal
 * actions used below. Avoid widening to the full redux Dispatch — callers
 * passing saga-enhanced dispatch or test mocks would otherwise be coerced
 * into a contract this function does not actually exercise.
 */
type ModalDispatch = (
  action: ReturnType<typeof showConfirmModal> | ReturnType<typeof hideModal>
) => unknown;

/**
 * Central target for all "manage your plan" navigation.
 *
 * The /onboarding/manage-plan page is server-rendered in schedugram-server.
 * From the React client we navigate via full-page window.location.href so the
 * server can serve the page fresh (it handles plan selection + payment method
 * collection via Chargebee hosted page).
 *
 * Every upsell / upgrade / limit-reached / "pick a plan" CTA in the dashboard
 * should go through goToManagePlan() rather than opening a plan-quote modal.
 */
export const MANAGE_PLAN_URL = "/onboarding/manage-plan";

interface GoToManagePlanOptions {
  /**
   * Highlight a specific add-on on the manage-plan page (e.g.
   * `post-status-approvals-addon-basic` for Internal Approvals,
   * `Approvals-advanced-addon` for External Approvals). The server reads the
   * `addon` query string and pre-selects/scrolls to the matching item.
   */
  addon?: string;
  /** Where the upgrade started, for upsell attribution on the manage-plan page. */
  source?: string;
}

export const goToManagePlan = (options: GoToManagePlanOptions = {}): void => {
  // `encodeURIComponent`, not URLSearchParams: the latter encodes a space as
  // "+", which would change the addon URLs existing callers produce.
  const query = (["addon", "source"] as const)
    .filter((key) => options[key])
    .map((key) => `${key}=${encodeURIComponent(options[key] as string)}`)
    .join("&");
  navigateToUrl(query ? `${MANAGE_PLAN_URL}?${query}` : MANAGE_PLAN_URL);
};

type ManagePlanConfirmOptions = {
  title?: string;
  message?: ReactNode;
  confirmText?: string;
  cancelText?: string;
};

/**
 * Use this from authoring flows where leaving for billing can drop unsaved
 * changes. It follows the existing dashboard confirm-modal pattern.
 *
 * The primary (confirm) button is the destructive action that navigates the
 * user away — that matches the dashboard convention for confirm modals.
 */
export const confirmAndGoToManagePlan = (
  dispatch: ModalDispatch,
  {
    title = "Leave this page?",
    message = "Are you sure you want to leave this page? Any unsaved work will be lost.",
    confirmText = "Leave page",
    cancelText = "Keep editing",
  }: ManagePlanConfirmOptions = {}
): void => {
  dispatch(
    showConfirmModal({
      title,
      message,
      confirmText,
      cancelText,
      confirmButtonProps: {
        variant: "blue-secondary",
      },
      handleConfirm() {
        dispatch(hideModal(true));
        goToManagePlan();
      },
      handleCancel() {
        dispatch(hideModal(false, "CONFIRM_MODAL"));
      },
    })
  );
};
